import { useEffect, useState } from "react";
import API from "@/lib/api";
import { Users, Loader2, Check, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

export default function ForumSettingsCard() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [avatarMode, setAvatarMode] = useState("initial");
  const [nameCheck, setNameCheck] = useState({ status: "idle", reason: "" });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    try {
      const { data } = await API.get("/forum/me");
      setMe(data);
      setDisplayName(data.display_name || "");
      setAvatarMode(data.avatar_mode || "initial");
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const checkName = async (name) => {
    if (name.trim().length < 2) { setNameCheck({ status: "invalid", reason: "Must be at least 2 characters" }); return; }
    if (name.trim().toLowerCase() === (me?.display_name || "").toLowerCase()) { setNameCheck({ status: "idle", reason: "" }); return; }
    setNameCheck({ status: "checking", reason: "" });
    try {
      const { data } = await API.post("/forum/check-display-name", { display_name: name });
      setNameCheck({ status: data.available ? "available" : "taken", reason: data.reason || (data.available ? "" : "That name is already taken") });
    } catch { setNameCheck({ status: "idle", reason: "" }); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (displayName.trim() && displayName.trim() !== me.display_name) payload.display_name = displayName.trim();
      if (avatarMode !== me.avatar_mode) payload.avatar_mode = avatarMode;
      if (Object.keys(payload).length === 0) { setEditing(false); setSaving(false); return; }
      await API.put("/forum/me", payload);
      toast({ title: "Forum settings updated" });
      await load();
      setEditing(false);
    } catch (err) {
      toast({ title: "Couldn't save", description: err?.response?.data?.detail || "", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;
  if (!me?.has_access) return null;

  // Not consented yet → show CTA to join
  if (!me.consented) {
    return (
      <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3" data-testid="forum-settings-join-cta">
        <div className="w-9 h-9 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center shrink-0">
          <Users className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">ADHD Club Forum</p>
          <p className="text-xs text-muted-foreground">Join the community and pick your display name.</p>
        </div>
        <Link to="/forum"><Button size="sm" className="rounded-full">Join</Button></Link>
      </div>
    );
  }

  if (!editing) {
    return (
      <div className="bg-card border border-border rounded-xl p-4" data-testid="forum-settings-card">
        <button onClick={() => setEditing(true)} className="w-full flex items-center gap-3 text-left" data-testid="forum-settings-open-button">
          <div className="w-9 h-9 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center shrink-0">
            <Users className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">ADHD Club Forum</p>
            <p className="text-xs text-muted-foreground truncate">
              Display name: <span className="font-medium text-foreground">{me.display_name}</span> · Avatar: {me.avatar_mode === "picture" ? "Profile picture" : "Initial only"}
            </p>
          </div>
          <span className="text-sm text-primary font-medium shrink-0">Edit</span>
        </button>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-4 space-y-3" data-testid="forum-settings-form">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center shrink-0">
          <Users className="w-4 h-4" />
        </div>
        <p className="font-semibold text-sm">ADHD Club Forum settings</p>
      </div>

      <div>
        <Label className="text-xs font-medium">Display name</Label>
        <Input
          value={displayName}
          onChange={(e) => { setDisplayName(e.target.value); checkName(e.target.value); }}
          maxLength={24}
          className="mt-1 h-10"
          data-testid="forum-settings-display-name-input"
        />
        <div className="text-xs mt-1 h-5 flex items-center gap-1">
          {nameCheck.status === "checking" && (<><Loader2 className="w-3 h-3 animate-spin" /> checking…</>)}
          {nameCheck.status === "available" && (<><CheckCircle2 className="w-3 h-3 text-green-600" /> <span className="text-green-700">available</span></>)}
          {(nameCheck.status === "taken" || nameCheck.status === "invalid") && (
            <><XCircle className="w-3 h-3 text-red-600" /> <span className="text-red-700">{nameCheck.reason}</span></>
          )}
        </div>
      </div>

      <div>
        <Label className="text-xs font-medium">Avatar display</Label>
        <div className="grid grid-cols-2 gap-2 mt-1">
          <button
            type="button"
            onClick={() => setAvatarMode("initial")}
            className={`p-3 rounded-lg border-2 text-left text-sm transition-colors ${avatarMode === "initial" ? "border-primary bg-primary/5" : "border-border hover:bg-accent"}`}
            data-testid="forum-settings-avatar-initial"
          >
            <div className="flex items-center gap-2">
              Just initial
              {avatarMode === "initial" && <Check className="w-3.5 h-3.5 text-primary ml-auto" />}
            </div>
          </button>
          <button
            type="button"
            onClick={() => setAvatarMode("picture")}
            className={`p-3 rounded-lg border-2 text-left text-sm transition-colors ${avatarMode === "picture" ? "border-primary bg-primary/5" : "border-border hover:bg-accent"}`}
            data-testid="forum-settings-avatar-picture"
          >
            <div className="flex items-center gap-2">
              Profile picture
              {avatarMode === "picture" && <Check className="w-3.5 h-3.5 text-primary ml-auto" />}
            </div>
          </button>
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <Button
          variant="outline"
          className="flex-1 rounded-full"
          onClick={() => { setEditing(false); setDisplayName(me.display_name || ""); setAvatarMode(me.avatar_mode); setNameCheck({ status: "idle", reason: "" }); }}
          disabled={saving}
          data-testid="forum-settings-cancel"
        >
          Cancel
        </Button>
        <Button
          className="flex-1 rounded-full"
          onClick={save}
          disabled={saving || (displayName.trim() !== me.display_name && nameCheck.status !== "available")}
          data-testid="forum-settings-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </Button>
      </div>
    </div>
  );
}
